import React, { useEffect, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import Cookies from "js-cookie";
import { ClerkProvider } from "@clerk/clerk-react";
import { FeatureFlagsProvider } from "../components/context/FeatureFlagsContext";
import { UserProvider } from "../components/context/UserContext";
import { CurrencyProvider } from "../components/context/CurrencyContext";
import { dataDogInit } from "../events/datadog";
import "../styles/main.scss";
import { AvoSetup } from "../events/segment/segment";
import {
  COOKIES,
  getCookieDomain,
  REFERRER_COOKIE_OPTIONS,
} from "../events/cookies";
import LiveChat from "../components/LiveChat";
import {
  mobileAppMountEvents,
  mobileAppDismountEvents,
} from "../events/mobileApp/mobileAppEvents";
import { MobileAppContext } from "../components/context/MobileAppContext";
import { AuthAxiosInitializer } from "../API/AuthAxiosInitializer";

const setupCookies = (): void => {
  const domain = getCookieDomain();

  Cookies.set(COOKIES.REDIRECT_URL, window.location.href, { domain });
};

const SupplierPortal = ({ Component, pageProps }): React.ReactElement => {
  const [isMobileAppListening, setIsMobileAppListening] = useState(false);
  const router = useRouter();

  const isMobileApp =
    Cookies.get(COOKIES.MOBILE_APP) === "1" ||
    process?.env?.NEXT_PUBLIC_MOBILE_APP_FORCE === "true";

  useEffect(() => {
    if (router?.query?.referrer) {
      if (Cookies.get(COOKIES.REFERRER)) {
        Cookies.remove(COOKIES.REFERRER);
      }

      Cookies.set(
        COOKIES.REFERRER,
        `${router.query.referrer}`,
        REFERRER_COOKIE_OPTIONS
      );
    }
  }, [router?.query?.referrer]);

  useEffect(() => {
    dataDogInit();

    setupCookies();

    if (isMobileApp) {
      mobileAppMountEvents();
      setIsMobileAppListening(true);
      return () => {
        mobileAppDismountEvents();
        setIsMobileAppListening(false);
      };
    }
  }, []);

  AvoSetup();

  return (
    <>
      <Head>
        {!isMobileApp && process.env.NEXT_PUBLIC_POWERFRONT_ENABLED === "true" && (
          <script
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: `
                (function(u,t,d){
                var i=d.createElement(t);i.type='text/javascript';i.async=true;i.src='//'+u;
                var s=d.getElementsByTagName(t)[0]; s.parentNode.insertBefore(i,s);
                })('${
                  process.env.NEXT_PUBLIC_ENV === "prod"
                    ? "us-cdn.inside-graph.com/gtm/IN-1001180/include.js"
                    : "us-sandbox-track.inside-graph.com/gtm/IN-1001179/include.js"
                }','script',document);
                `,
            }}
          />
        )}
      </Head>
      <Script src="https://cdn.optimizely.com/js/18185992549.js" />
      <FeatureFlagsProvider>
        <ClerkProvider
          publishableKey={process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY}
        >
          <AuthAxiosInitializer />
          <UserProvider>
            <CurrencyProvider>
              <MobileAppContext.Provider value={isMobileAppListening}>
                <Component {...pageProps} />
              </MobileAppContext.Provider>
            </CurrencyProvider>
          </UserProvider>
        </ClerkProvider>
      </FeatureFlagsProvider>

      {process.env.NEXT_PUBLIC_CHATBOT_ENABLED !== "false" && (
        <LiveChat embedded />
      )}
    </>
  );
};

export default SupplierPortal;
